import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { VerticalPageJobType } from 'modules/job-types/types';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import CaseStudyCard from './components/CaseStudyCard';
import { useSelector } from 'react-redux';
import {
  caseStudiesPageSizeSelector,
  caseStudiesSelector,
  caseStudiesTotalCountSelector,
} from 'modules/marketing-pages/duck/selectors';

import CardList from 'components/organisms/CardList';
import { CaseStudy } from 'modules/marketing-pages/marketing-page.types';
import { isMobile } from 'utils/layout';

interface Props {
  currentPage: number;
  jobType: VerticalPageJobType;
  onCurrentPageChange: (pageNumber: number) => void;
  onLoadMore: (param: { newPage: number }) => void;
  translationId: string;
}

const ListCaseStudies: FC<Props> = ({ jobType, currentPage, onLoadMore, onCurrentPageChange }) => {
  const { t } = useTranslation('marketingPages');
  const { query } = useRouter();
  const pageQuery = useMemo(() => Number(query.caseStudyPage ?? '1'), [query.caseStudyPage]);
  const totalCaseStudies = useSelector(caseStudiesTotalCountSelector);
  const caseStudies = useSelector(caseStudiesSelector);
  const pageSize = useSelector(caseStudiesPageSizeSelector);
  const sectionContainerRef = useRef<HTMLDivElement>(null);

  const onPageChange = useCallback(
    (newPage: number) => {
      const noCaseStudiesForNewPage =
        caseStudies.length > 0 && caseStudies.every(({ page }) => page !== newPage);
      const mobileView = isMobile(window.screen.width);
      // Scroll to Pro list section top.
      // This is very important to mitigate CLS while page has changed,
      // because CLS is counted for view port only, so if we scroll to Section Top
      // then show loading placeholders which then made to occupy most of the view port
      // CLS will be minimal to none.
      if ((mobileView && currentPage === 1) || !mobileView) {
        sectionContainerRef.current?.scrollIntoView();
      }

      if (noCaseStudiesForNewPage) {
        onLoadMore({ newPage });
      }
      onCurrentPageChange(newPage);
    },
    [onLoadMore, onCurrentPageChange, caseStudies, sectionContainerRef.current, currentPage]
  );

  useEffect(() => {
    if (pageQuery !== currentPage) {
      onPageChange(pageQuery);
    }
  }, [currentPage, pageQuery, onPageChange]);

  if (_.isEmpty(jobType)) {
    return null;
  }

  return (
    <CardList
      cardRenderer={(dataItem) => <CaseStudyCard caseStudy={dataItem as CaseStudy} />}
      currentPage={currentPage}
      data={caseStudies}
      loadingPlaceholderHeight="60vh"
      pageQueryParamName="caseStudyPage"
      pageSize={pageSize}
      ref={sectionContainerRef}
      testId="list-case-studies"
      title={t('caseStudySection.title')}
      totalItems={totalCaseStudies}
    />
  );
};

export type { Props as ListCaseStudiesProps };
export default ListCaseStudies;
